import { Download, FileText } from "lucide-react";
import { ZoomablePanImage } from "./ZoomablePanImage";

/**
 * Renders one {@code ToolVisual} from the gateway stream, branching on
 * mimeType — because not every visual is an image. A report rendered as
 * PDF (or Excel) arrives as a {@code ToolVisual} too, and {@code <img>}
 * obviously can't display it. The previous code path passed everything
 * straight to {@link ZoomablePanImage} (image-only) which showed PDFs as
 * a broken-image icon — see screenshot in the 2026-05-26 thread.
 *
 * <p>Behaviour per mimeType:
 * <ul>
 *   <li><b>{@code image/*}</b> — {@link ZoomablePanImage} (unchanged).</li>
 *   <li><b>{@code application/pdf}</b> — native browser PDF viewer via
 *       {@code <embed type="application/pdf">}, with a "Download PDF"
 *       link below as a fallback for environments without an inline
 *       PDF plugin.</li>
 *   <li><b>Other binaries</b> (Excel, CSV, …) — a download card with the
 *       filename + a Download button; browsers don't render xlsx inline.</li>
 * </ul>
 */
export function ToolVisualBlock({
  mimeType,
  dataUrl,
  index,
}: {
  mimeType: string;
  dataUrl: string;
  /** 1-based index into the turn's visuals; used for the default filename. */
  index: number;
}) {
  const mt = (mimeType ?? "").trim().toLowerCase();

  if (mt.startsWith("image/")) {
    return <ZoomablePanImage src={dataUrl} alt={`Tool visual ${index}`} />;
  }

  if (mt === "application/pdf") {
    const filename = `datumcraft-document-${index}.pdf`;
    return (
      <div className="relative overflow-hidden rounded-lg border border-datumcraft-slate/25 bg-white">
        <embed
          src={dataUrl}
          type="application/pdf"
          className="block h-[600px] w-full"
          aria-label={`PDF document ${index}`}
        />
        <DownloadLink
          dataUrl={dataUrl}
          filename={filename}
          mimeType="PDF"
          label="Download PDF"
        />
      </div>
    );
  }

  // Fallback for non-image, non-PDF binaries (Excel, CSV, etc.). The
  // browser can't render these inline; offer a download instead of
  // showing nothing.
  const ext = extensionFor(mt);
  const friendly = friendlyFor(mt);
  const filename = `datumcraft-document-${index}${ext}`;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-datumcraft-slate/25 bg-white p-3">
      <FileText className="h-8 w-8 shrink-0 text-datumcraft-slate" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-datumcraft-navy">{filename}</div>
        <div className="text-xs text-datumcraft-slate">{friendly}</div>
      </div>
      <a
        href={dataUrl}
        download={filename}
        className="inline-flex items-center gap-1.5 rounded-md border border-datumcraft-slate/30 bg-white px-2.5 py-1 text-xs font-medium text-datumcraft-navy hover:bg-datumcraft-mist"
      >
        <Download className="h-3.5 w-3.5" aria-hidden />
        Download
      </a>
    </div>
  );
}

/** Small "Download PDF" pill anchored bottom-right of the embed viewer. */
function DownloadLink({
  dataUrl,
  filename,
  mimeType,
  label,
}: {
  dataUrl: string;
  filename: string;
  mimeType: string;
  label: string;
}) {
  return (
    <div className="absolute bottom-2 right-2">
      <a
        href={dataUrl}
        download={filename}
        title={`Save as ${mimeType}`}
        className="inline-flex items-center gap-1.5 rounded-md border border-datumcraft-slate/30 bg-white/95 px-2.5 py-1 text-xs font-medium text-datumcraft-navy shadow-sm backdrop-blur hover:bg-datumcraft-mist"
      >
        <Download className="h-3.5 w-3.5" aria-hidden />
        {label}
      </a>
    </div>
  );
}

/** Best-effort file extension for the download filename, by mimeType. */
function extensionFor(mt: string): string {
  switch (mt) {
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return ".xlsx";
    case "application/vnd.ms-excel":
      return ".xls";
    case "text/csv":
      return ".csv";
    case "application/json":
      return ".json";
    case "application/xml":
    case "text/xml":
      return ".xml";
    case "text/plain":
      return ".txt";
    case "application/zip":
      return ".zip";
    default:
      return ".bin";
  }
}

/** Human-friendly label shown next to the download button. */
function friendlyFor(mt: string): string {
  switch (mt) {
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return "Excel spreadsheet";
    case "application/vnd.ms-excel":
      return "Excel spreadsheet (legacy)";
    case "text/csv":
      return "CSV file";
    case "application/json":
      return "JSON document";
    case "application/xml":
    case "text/xml":
      return "XML document";
    case "text/plain":
      return "Text document";
    case "application/zip":
      return "Archive";
    default:
      return mt || "Binary file";
  }
}
